// src/components/charts/PlacesParPortStackedBar.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../../services/api';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, BarElement, Tooltip, Legend,
  ChartOptions, ChartData
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

type Props = {
  date: string;
  limit?: number;
  sortBy?: 'libres' | 'occupes' | 'total';
  className?: string;
  height?: number;
};

type PortRow = {
  port: string;            // normalisé depuis nomport
  total_places: number;
  occupied_places: number;
  free_places: number;
};

type ApiResponse = {
  date: string;
  resume?: { total_places: number; occupied_places: number; free_places: number };
  ports?: Array<{
    nomport: string;
    total_places: number;
    occupied_places: number;
    free_places: number;
  }>;
};

function fmtInt(n: number) {
  return new Intl.NumberFormat('fr-FR').format(n || 0);
}

export default function PlacesParPortStackedBar({
  date,
  limit = 15,
  sortBy = 'libres',
  className = '',
  height = 360,
}: Props) {
  const [rows, setRows] = useState<PortRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setErr('');
        const res = await api.get<ApiResponse>('/places-libres-par-port', { params: { date } });

        // 🔹 Normalisation: ports[] -> PortRow[]
        const ports = res.data?.ports ?? [];
        const normalized: PortRow[] = ports.map(p => ({
          port: p.nomport ?? '—',
          total_places: Number(p.total_places ?? 0),
          occupied_places: Number(p.occupied_places ?? 0),
          free_places: Number(p.free_places ?? 0),
        }));

        if (!cancelled) setRows(normalized);
      } catch (e: any) {
        if (!cancelled) setErr(e?.response?.data?.error || 'Erreur de chargement');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [date]);

  const sorted = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      if (sortBy === 'total')   return (b.total_places   ?? 0) - (a.total_places   ?? 0);
      if (sortBy === 'occupes') return (b.occupied_places?? 0) - (a.occupied_places?? 0);
      return (b.free_places     ?? 0) - (a.free_places     ?? 0);
    });
    return copy.slice(0, limit);
  }, [rows, limit, sortBy]);

  const data: ChartData<'bar'> | null = useMemo(() => {
    if (!sorted.length) return null;
    const labels = sorted.map(r => r.port);

    return {
      labels,
      datasets: [
        {
          label: 'Libres',
          data: sorted.map(r => r.free_places),
          backgroundColor: '#b5ecc9',
          borderColor: '#b5ecc9',
          borderWidth: 1,
          stack: 'places',
          barThickness: 45,
        },
        {
          label: 'Occupées',
          data: sorted.map(r => r.occupied_places),
          backgroundColor: '#ff6060',
          borderColor: '#ff6060',
          borderWidth: 1,
          stack: 'places',
          barThickness: 45,
        },
      ],
    };
  }, [sorted]);

  const options: ChartOptions<'bar'> = useMemo(() => ({
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top', labels: { usePointStyle: true } },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: (ctx) => {
            const label = ctx.dataset.label || '';
            const val = typeof ctx.parsed.x === 'number' ? fmtInt(ctx.parsed.x) : '-';
            // total de la ligne
            const i = ctx.dataIndex;
            const libres = (ctx.chart.data.datasets[0].data as number[])[i] ?? 0;
            const occup  = (ctx.chart.data.datasets[1].data as number[])[i] ?? 0;
            return ` ${label}: ${val} (Total: ${fmtInt(libres + occup)})`;
          }
        }
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: { color: 'rgba(0,0,0,0.06)' },
        ticks: { callback: (v) => fmtInt(Number(v)) },
        title: { display: true, text: 'Places' },
      },
      y: { stacked: true, grid: { display: false } },
    },
  }), []);

  return (
    <div className={`rounded-2xl border border-gray-200 bg-white px-5 pb-4 pt-5 dark:border-darkBorderCard dark:bg-white/[0.03] sm:px-6 sm:pt-6 ${className}`}>
      <div className="mb-4 flex items-center justify-between gap-2">
        <div>
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
            Places par port (libres vs occupées)
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Date : {date} • Tri : {sortBy === 'libres' ? 'Places libres' : sortBy === 'occupes' ? 'Places occupées' : 'Total'}
          </p>
        </div>
      </div>

      {loading && <div className="text-gray-500">Chargement…</div>}
      {err && !loading && <div className="text-red-600">{err}</div>}

      {!loading && !err && data && rows.length > 0 && (
        <div style={{ height }}>
          <Bar data={data} options={options} />
        </div>
      )}

      {!loading && !err && rows.length === 0 && (
        <div className="text-gray-500">Aucune donnée.</div>
      )}
    </div>
  );
}
